import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Trophy, Check, Clock } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { RARITY } from "@/lib/game";

function RewardChips({ reward }) {
  const rows = [];
  for (const kind of ["weapon", "armor"])
    for (const [rarity, count] of Object.entries(reward[kind] || {}))
      if (count) rows.push({ kind, rarity, count });
  return (
    <div className="flex flex-wrap gap-1.5">
      {reward.gold ? (
        <span className="text-[10px] px-1.5 py-0.5 border rounded-sm text-amber-300 border-amber-600/50 bg-amber-950/30 flex items-center gap-1">
          <GoldCoin size={10} /> +{reward.gold} gold
        </span>
      ) : null}
      {reward.follis ? (
        <span className="text-[10px] px-1.5 py-0.5 border rounded-sm text-amber-600 border-amber-800/60 bg-gradient-to-r from-amber-950/50 to-orange-950/30 flex items-center gap-1">
          <FollisCoin size={10} /> +{reward.follis} Follis
        </span>
      ) : null}
      {rows.map((r, i) => {
        const m = RARITY[r.rarity] || RARITY.common;
        return (
          <span key={i} className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${m.text} ${m.border} ${m.bg}`}>
            +{r.count} {r.rarity} {r.kind} fragment{r.count > 1 ? "s" : ""}
          </span>
        );
      })}
    </div>
  );
}

function timeUntil(iso) {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "soon";
  const h = Math.floor(ms / 3.6e6), d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  const m = Math.floor((ms % 3.6e6) / 6e4);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function QuestCard({ q, onClaim, busy }) {
  const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
  return (
    <div data-testid={`quest-${q.id}`} className={`border p-3 rounded-sm transition-colors ${
      q.claimed ? "border-zinc-800 bg-zinc-900/30 opacity-70"
      : q.completed ? "border-emerald-700/50 bg-emerald-950/15"
      : "border-zinc-800 bg-zinc-950"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-cinzel text-sm text-amber-200 tracking-wide">{q.label}</div>
          <div className="text-[11px] text-zinc-500">{q.desc}</div>
        </div>
        {q.claimed && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1 shrink-0">
            <Check size={11} /> Claimed
          </span>
        )}
      </div>
      <div className="mt-2 mb-2.5">
        <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
          <span className="uppercase tracking-widest">Progress</span>
          <span className="tabular-nums">{q.progress}/{q.target}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${q.completed ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <RewardChips reward={q.reward} />
        {!q.claimed && (
          <button data-testid={`claim-${q.id}`} disabled={!q.completed || busy} onClick={() => onClaim(q.id)}
            className={`text-xs px-3 py-1.5 rounded-sm font-cinzel uppercase tracking-wide transition-colors shrink-0 ${
              q.completed ? "bg-amber-600 hover:bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-600 cursor-not-allowed"}`}>
            Claim
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ title, reset, list, onClaim, busy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] uppercase tracking-widest text-zinc-400 font-cinzel">{title}</h3>
        <span className="text-[10px] text-zinc-600 flex items-center gap-1"><Clock size={10} /> resets in {timeUntil(reset)}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {list.map((q) => <QuestCard key={q.id} q={q} onClaim={onClaim} busy={busy} />)}
      </div>
    </div>
  );
}

export default function QuestPanel({ onClaimed }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const load = () => api.get("/quests").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/quests/${id}/claim`);
      const parts = [];
      if (res?.reward?.gold) parts.push(`+${res.reward.gold} gold`);
      if (res?.reward?.follis) parts.push(`+${res.reward.follis} Follis`);
      const message = parts.length > 0 ? `Claimed ${parts.join(" & ")}!` : "Reward claimed — fragments added to your forge!";
      toast.success(message);
      load();
      onClaimed && onClaimed();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const claimAll = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/quests/claim-all");
      const parts = [`Claimed ${res.count} reward${res.count > 1 ? "s" : ""}`];
      if (res.gold) parts.push(`+${res.gold} gold`);
      if (res.follis) parts.push(`+${res.follis} Follis`);
      toast.success(parts.join(" · "));
      load();
      onClaimed && onClaimed();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <section data-testid="quest-panel" className="mb-8 border border-amber-900/30 bg-zinc-950/60 rounded-sm">
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-5 hover:bg-zinc-900/30 transition-colors"
      >
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
          <Trophy size={16} /> Daily &amp; Weekly Rewards
        </h2>
        <div className="flex items-center gap-3">
          {data.claimable > 0 && (
            <span data-testid="quest-claimable-badge" className="text-[10px] uppercase tracking-widest bg-amber-500 text-zinc-950 px-2 py-0.5 rounded-full font-bold">
              {data.claimable} ready
            </span>
          )}
          {isCollapsed ? <ChevronDown size={18} className="text-zinc-500" /> : <ChevronUp size={18} className="text-zinc-500" />}
        </div>
      </button>
      
      {!isCollapsed && (
        <div className="px-5 pb-5 pt-2">
          {data.claimable > 0 && (
            <div className="flex justify-end mb-4">
              <button data-testid="claim-all-btn" onClick={claimAll} disabled={busy}
                className="text-xs px-3 py-1.5 rounded-sm font-cinzel uppercase tracking-wide bg-amber-600 hover:bg-amber-500 text-zinc-950 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                <Check size={12} /> Claim All
              </button>
            </div>
          )}
          <div className="space-y-5">
            <Section title="Daily" reset={data.daily_reset} list={data.daily} onClaim={claim} busy={busy} />
            <Section title="Weekly" reset={data.weekly_reset} list={data.weekly} onClaim={claim} busy={busy} />
          </div>
        </div>
      )}
    </section>
  );
}
